import {
    cn,
    formatMoney,
    listSpec,
    oneSpec,
    useAuthStore,
    useIsPlatformHost,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Badge,
    DoneIcon as CircleCheck,
    DateTime,
    EmptyState,
    PageHeader,
    Section,
    Skeleton,
    StatusBadge,
} from '@bemor/web-kit/ui';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { Alert, Approval, Now, QueueCount, Tenant } from '@/api/types';
import { OpenTenant } from '@/components/open-tenant';
import { queueLabel, QUEUES } from '@/layouts/app-layout';
import { approvalLabel } from '@/lib/approvals';
import { P } from '@/lib/permissions';
import { useList, useOne } from '@/lib/queries';
import { useMe, useStaffDirectory, whoName } from '@/lib/staff';
import { brandName } from '@/lib/tenants';

/** A queue and its open count (design `BoQueueCard`): Signal when something is open, quiet when not. */
function QueueCard({ q, loading }: { q: QueueCount; loading: boolean }) {
    return (
        <Link
            to={`/queues/${q.queue}`}
            className="border-border-1 bg-surface-card text-fg-1 hover:border-border-hover flex flex-col gap-2 rounded-lg border p-4 no-underline"
        >
            <span className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">
                    {queueLabel(q.queue)}
                </span>
                {loading ? (
                    <Skeleton className="h-6 w-8" />
                ) : (
                    <span
                        className={cn(
                            'grid h-6 min-w-8 place-items-center rounded-full px-2 text-sm font-semibold tabular-nums',
                            q.open > 0
                                ? 'bg-signal-500 text-white'
                                : 'bg-surface-pressed text-fg-2',
                        )}
                    >
                        {q.open}
                    </span>
                )}
            </span>
            <span className="text-fg-2 text-xs">
                {loading
                    ? ''
                    : q.open > 0
                      ? 'Open items waiting'
                      : 'Nothing open'}
            </span>
        </Link>
    );
}

/** Hairline rows inside a section (design `DbRows`). */
function Rows({
    rows,
    empty,
}: {
    rows: { key: string; title: ReactNode; sub: ReactNode; aside: ReactNode }[];
    empty: string;
}) {
    if (rows.length === 0)
        return <p className="text-fg-2 m-0 text-sm">{empty}</p>;
    return (
        <ul className="m-0 list-none p-0">
            {rows.map((r) => (
                <li
                    key={r.key}
                    className="border-border-1 flex items-baseline justify-between gap-4 py-3 not-first:border-t"
                >
                    <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="truncate text-sm">{r.title}</span>
                        <span className="text-fg-2 truncate text-xs">
                            {r.sub}
                        </span>
                    </span>
                    <span className="flex-none text-right text-sm">
                        {r.aside}
                    </span>
                </li>
            ))}
        </ul>
    );
}

const viewAll = (to: string) => (
    <Link to={to} className="text-sm">
        View all
    </Link>
);

/** The open alerts as rows (home and, BM-68, the platform host's home). */
const alertRows = (alerts: Alert[]) =>
    alerts.map((a) => ({
        key: a.id,
        title: (
            <span className="inline-flex items-center gap-2">
                <Badge variant={a.severity === 'urgent' ? 'danger' : 'warning'}>
                    {a.severity === 'urgent' ? 'Urgent' : 'Warning'}
                </Badge>
                {a.rule_key.replace(/[._]/g, ' ')}
            </span>
        ),
        sub: (
            <>
                {a.merchant_id ?? 'Platform'}, raised{' '}
                <DateTime value={a.raised_at} empty="—" />
            </>
        ),
        aside: a.state === 'acknowledged' ? 'Acknowledged' : 'Open',
    }));

/** BM-68: the reads of `/` on the platform host: the tenants and the platform-wide alerts (spec 2.5). */
export const platformHomeReads = () => ({
    tenants: listSpec('/staff/tenants', { per_page: 50 }),
    alerts: listSpec('/staff/alerts', { state: 'open', limit: 5 }),
});

/** `/` on the platform host: the tenants, each with Open admin / Open Ledger, and the platform-wide alerts. */
function PlatformHome() {
    const can = useAuthStore((s) => s.can);
    const reads = platformHomeReads();
    const tenants = useList<Tenant>(
        reads.tenants.path,
        reads.tenants.params,
        can(P.tenantsManage),
    );
    const alerts = useList<Alert>(
        reads.alerts.path,
        reads.alerts.params,
        can(P.alertsRead),
    );
    return (
        <div className="flex flex-col gap-6">
            <PageHeader
                title="Platform"
                meta={<AsOfBadge asOf={tenants.freshness.asOf} />}
            />
            <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
                {can(P.tenantsManage) ? (
                    <Section
                        title="Tenants"
                        action={viewAll('/tenants')}
                        testId="platform-tenants"
                    >
                        <Rows
                            empty={
                                tenants.isPending
                                    ? 'Loading the tenants…'
                                    : 'No tenants yet.'
                            }
                            rows={(tenants.data?.data.items ?? []).map((t) => ({
                                key: t.id,
                                title: (
                                    <Link to={`/tenants/${t.id}`}>
                                        {brandName(t)}
                                    </Link>
                                ),
                                sub: (
                                    <span className="inline-flex items-center gap-2">
                                        <span className="font-mono">
                                            {t.slug}
                                        </span>
                                        <StatusBadge
                                            status={t.status}
                                            domain="tenant"
                                        />
                                    </span>
                                ),
                                aside: <OpenTenant tenant={t} />,
                            }))}
                        />
                    </Section>
                ) : null}
                {can(P.alertsRead) ? (
                    <Section
                        title="Platform alerts"
                        action={viewAll('/alerts')}
                    >
                        <Rows
                            empty={
                                alerts.isPending
                                    ? 'Loading the alerts…'
                                    : 'No open alerts.'
                            }
                            rows={alertRows(alerts.data?.data.items ?? [])}
                        />
                    </Section>
                ) : null}
            </div>
        </div>
    );
}

/** BM-122 decision 4 (Nexroll `dashboard/index.tsx` stat cards): one tile of the Today strip, a link to its page. */
function TodayTile({
    to,
    label,
    value,
    sub,
    loading,
    testId,
}: {
    to: string;
    label: string;
    value: ReactNode;
    sub?: ReactNode;
    loading: boolean;
    testId: string;
}) {
    return (
        <Link
            to={to}
            data-testid={testId}
            className="border-border-1 bg-surface-card text-fg-1 hover:border-border-hover flex flex-col gap-1 rounded-lg border p-3 no-underline"
        >
            <span className="text-fg-2 text-xs font-medium">{label}</span>
            {loading ? (
                <Skeleton className="h-6 w-16" />
            ) : (
                <span className="text-lg leading-tight font-semibold tabular-nums">
                    {value}
                </span>
            )}
            {sub ? <span className="text-fg-2 text-xs">{sub}</span> : null}
        </Link>
    );
}

/** The tenant today (`/staff/reports/now`): today's figures in the business day and the open counts, above the queues. */
function TodayStrip() {
    const now = useOne<Now>(nowRead().path);
    const d = now.data?.data;
    const loading = now.isPending;
    const bps = (v: number | null | undefined) =>
        v == null ? '—' : `${(v / 100).toFixed(1)}%`;
    const pending = d?.outbox.reduce((n, o) => n + o.pending, 0) ?? 0;
    const oldest =
        d?.outbox.reduce<number | null>(
            (max, o) =>
                o.oldest_age_s != null && (max === null || o.oldest_age_s > max)
                    ? o.oldest_age_s
                    : max,
            null,
        ) ?? null;
    return (
        <section data-testid="today-strip" className="flex flex-col gap-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="m-0 text-sm font-medium">
                    Today
                    {d ? (
                        <span className="text-fg-2 font-normal">
                            , {d.business_day_label}
                        </span>
                    ) : null}
                </h2>
                <Link to="/reports/metrics" className="text-sm">
                    Metrics
                </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
                <TodayTile
                    to="/reports/finance"
                    label="Orders"
                    value={d?.orders ?? 0}
                    loading={loading}
                    testId="today-orders"
                />
                <TodayTile
                    to="/reports/finance"
                    label="Gross"
                    value={formatMoney(d?.gross_cents ?? 0, 'USD')}
                    sub={
                        d
                            ? `refunded ${formatMoney(d.refunded_cents, 'USD')}`
                            : undefined
                    }
                    loading={loading}
                    testId="today-gross"
                />
                <TodayTile
                    to="/reports/finance"
                    label="Fee income"
                    value={formatMoney(d?.income_cents ?? 0, 'USD')}
                    loading={loading}
                    testId="today-income"
                />
                <TodayTile
                    to="/reports/metrics"
                    label="Approval rate"
                    value={bps(d?.approval_rate_bps)}
                    sub={d ? `${d.attempts} attempts` : undefined}
                    loading={loading}
                    testId="today-approval"
                />
                <TodayTile
                    to="/disputes?status[]=open"
                    label="Open disputes"
                    value={d?.open_disputes ?? 0}
                    sub={
                        d
                            ? formatMoney(d.open_disputes_cents, 'USD')
                            : undefined
                    }
                    loading={loading}
                    testId="today-disputes"
                />
                {d?.alerts ? (
                    <TodayTile
                        to="/alerts"
                        label="Open alerts"
                        value={d.alerts.urgent + d.alerts.warning}
                        sub={`${d.alerts.urgent} urgent, ${d.alerts.warning} warning`}
                        loading={loading}
                        testId="today-alerts"
                    />
                ) : null}
                {d?.queues ? (
                    <TodayTile
                        to="/queues"
                        label="Open queue items"
                        value={d.queues.reduce((n, q) => n + q.open, 0)}
                        loading={loading}
                        testId="today-queues"
                    />
                ) : null}
                <TodayTile
                    to="/http-logs"
                    label="Outbox backlog"
                    value={pending}
                    sub={
                        oldest == null
                            ? 'nothing pending'
                            : `oldest ${oldest} s`
                    }
                    loading={loading}
                    testId="today-outbox"
                />
                <TodayTile
                    to="/alerts"
                    label="Alert evaluator"
                    value={
                        d?.evaluator_ran_at ? (
                            <DateTime
                                value={d.evaluator_ran_at}
                                format="relative"
                            />
                        ) : (
                            'never ran'
                        )
                    }
                    loading={loading}
                    testId="today-evaluator"
                />
            </div>
        </section>
    );
}

/**
 * `/` (design `backoffice/home`): the queues with their open counts in Signal, then the pending approvals and the
 * open alerts. A quiet day says so once.
 */
/** The reads of `/` (BM-46 §2.0); each is shown only with its permission, and one refused is just not seeded. */
/** BM-122: the Today strip's read. */
export const nowRead = () => oneSpec('/staff/reports/now');

export const homeReads = () => ({
    now: nowRead(),
    queues: listSpec('/staff/queues'),
    approvals: listSpec('/staff/approvals', { state: 'pending', limit: 5 }),
    alerts: listSpec('/staff/alerts', { state: 'open', limit: 5 }),
});

export function HomePage() {
    const platformHost = useIsPlatformHost();
    if (platformHost) return <PlatformHome />;
    return <TenantHome />;
}

function TenantHome() {
    const can = useAuthStore((s) => s.can);
    const me = useMe();
    const directory = useStaffDirectory();
    const reads = homeReads();
    const queues = useList<QueueCount>(
        reads.queues.path,
        reads.queues.params,
        can(P.queuesRead),
    );
    const approvals = useList<Approval>(
        reads.approvals.path,
        reads.approvals.params,
        can(P.approvalsRead),
    );
    const alerts = useList<Alert>(
        reads.alerts.path,
        reads.alerts.params,
        can(P.alertsRead),
    );
    const counts = queues.data?.data.items ?? [];
    const cards: QueueCount[] = QUEUES.map(
        (queue) => counts.find((c) => c.queue === queue) ?? { queue, open: 0 },
    );
    const quiet =
        !!queues.data &&
        counts.every((c) => c.open === 0) &&
        (approvals.data?.data.items.length ?? 0) === 0 &&
        (alerts.data?.data.items.length ?? 0) === 0;
    return (
        <div className="flex flex-col gap-6">
            <PageHeader
                title="Home"
                meta={<AsOfBadge asOf={queues.freshness.asOf} />}
            />
            {quiet ? (
                <div className="border-border-1 bg-surface-card rounded-lg border">
                    <EmptyState
                        icon={CircleCheck}
                        title="Nothing is open"
                        body="Every queue is clear and no approval or alert is waiting. New work shows up here and in the sidebar counts."
                    />
                </div>
            ) : null}
            {can(P.reportsRead) ? <TodayStrip /> : null}
            {can(P.queuesRead) ? (
                <div
                    className="grid grid-cols-2 gap-3 md:grid-cols-4"
                    data-testid="queue-counts"
                >
                    {cards.map((q) => (
                        <QueueCard
                            key={q.queue}
                            q={q}
                            loading={queues.isPending}
                        />
                    ))}
                </div>
            ) : null}
            {quiet ? null : (
                <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
                    {can(P.approvalsRead) ? (
                        <Section
                            title="Pending approvals"
                            action={viewAll('/approvals')}
                        >
                            <Rows
                                empty="No approvals waiting."
                                rows={(approvals.data?.data.items ?? []).map(
                                    (a) => ({
                                        key: a.id,
                                        title: (
                                            <Link to={`/approvals/${a.id}`}>
                                                {a.summary}
                                            </Link>
                                        ),
                                        sub: `${approvalLabel(a)}, requested by ${whoName(a.requested_by, me, directory)}`,
                                        aside: (
                                            <DateTime
                                                value={a.expires_at}
                                                empty="—"
                                            />
                                        ),
                                    }),
                                )}
                            />
                        </Section>
                    ) : null}
                    {can(P.alertsRead) ? (
                        <Section
                            title="Open alerts"
                            action={viewAll('/alerts')}
                        >
                            <Rows
                                empty="No open alerts."
                                rows={alertRows(alerts.data?.data.items ?? [])}
                            />
                        </Section>
                    ) : null}
                </div>
            )}
        </div>
    );
}
